export type VerificationState = "Verified" | "Needs Research" | "Incomplete";
export type Criticality = "A" | "B" | "C";
export type SpecClass = "OEM SPEC" | "AS-BUILT / INSTALLED" | "PLANT VALIDATED";

export interface SpecValue {
  label: string;
  value: string;
  unit?: string;
  class: SpecClass;
  verification: "OEM manual" | "Nameplate" | "Drawing" | "Measured in field" | "Plant estimate" | "Unknown";
  note?: string;
}

export interface Part {
  id: string;
  internalPn: string;
  oemPn: string;
  description: string;
  manufacturer: string;
  category: string;
  criticality: Criticality;
  stockQty: number;
  minQty: number;
  location: string;
  preferredVendorId?: string;
  vendorSku?: string;
  leadTime?: string;
  lastPrice?: number;
  alternates: string[];
}

export interface BomItem {
  partId: string;
  qty: number;
  locationNote?: string;
}

export interface Asset {
  id: string;
  tag: string;
  lineId: string;
  parentId?: string;
  order: number;
  name: string;
  type: string;
  manufacturer: string;
  model: string;
  serial: string;
  status: "In Service" | "Out of Service" | "Spare" | "Retired";
  criticality: Criticality;
  verificationState: VerificationState;
  completeness: number;
  guideTiId?: string;
  specs: SpecValue[];
  capabilities: { label: string; value: string }[];
  bom: BomItem[];
  notes?: string;
}

export interface Line {
  id: string;
  name: string;
  status: "Active" | "Placeholder";
  description: string;
  completeness: number;
}

export interface Vendor {
  id: string;
  name: string;
  website: string;
  contact: string;
  phone: string;
  email: string;
  preferred: boolean;
  notes: string;
}

export interface AuditEntry {
  id: string;
  at: string;
  user: string;
  action: string;
  entity: string;
  detail: string;
}

export interface PlantState {
  lines: Line[];
  assets: Asset[];
  parts: Part[];
  vendors: Vendor[];
  audit: AuditEntry[];
}

const plantLineNumbers = [1, 2, 4, 6, 7, 9, 11, 12, 14, 15, 16] as const;

const line1Flow = [
  ["feed", "L01-FEED-001", "Material Feed System", "Material Feed"],
  ["extruder", "L01-EXT-001", "Extruder", "Extruder"],
  ["vac1", "L01-VAC-001", "Vacuum Tank 1", "Vacuum Tank"],
  ["vac2", "L01-VAC-002", "Vacuum Tank 2", "Vacuum Tank"],
  ["cool", "L01-COOL-001", "Cooling Tank", "Cooling Tank"],
  ["puller", "L01-PUL-001", "Puller", "Puller"],
  ["printer", "L01-PRN-001", "Printer", "Printer"],
  ["saw", "L01-SAW-001", "Saw", "Saw"],
  ["beller", "L01-BEL-001", "Belling Table", "Belling Equipment"],
  ["pack", "L01-PKG-001", "Packaging Cart", "Packaging"]
] as const;

export const seedState: PlantState = {
  lines: plantLineNumbers.map((lineNumber) => ({
    id: `line-${lineNumber}`,
    name: `Line ${lineNumber}`,
    status: lineNumber === 1 ? "Active" : "Placeholder",
    description: lineNumber === 1 ? "Pilot extrusion downstream" : "Awaiting plant walkdown",
    completeness: lineNumber === 1 ? 42 : 0
  })),
  assets: [
    ...line1Flow.map(([id, tag, name, type], index): Asset => ({
      id,
      tag,
      lineId: "line-1",
      order: index,
      name,
      type,
      manufacturer: index === 1 ? "EXAMPLE OEM" : "Unknown",
      model: index === 1 ? "EXAMPLE MODEL" : "Unknown",
      serial: "Unknown",
      status: "In Service",
      criticality: index === 1 ? "A" : "B",
      verificationState: index === 1 ? "Needs Research" : "Incomplete",
      completeness: index === 1 ? 58 : 25,
      guideTiId: "",
      specs: index === 1 ? [
        { label: "Screw configuration", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Screw diameter", value: "Unknown", unit: "mm", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "L/D", value: "Unknown", class: "OEM SPEC", verification: "Unknown" },
        { label: "Continuous torque", value: "Unknown", unit: "Nm", class: "OEM SPEC", verification: "Unknown" },
        { label: "Thrust capacity", value: "Unknown", unit: "kN", class: "OEM SPEC", verification: "Unknown" },
        { label: "Rated throughput", value: "Unknown", unit: "kg/hr", class: "OEM SPEC", verification: "Unknown" },
        { label: "Plant validated throughput", value: "Unknown", unit: "kg/hr", class: "PLANT VALIDATED", verification: "Unknown" }
      ] : [],
      capabilities: index === 1 ? [
        { label: "PVC", value: "Needs validation" },
        { label: "CPVC", value: "Needs validation" }
      ] : [],
      bom: []
    })),
    {
      id: "ext-motor",
      tag: "L01-EXT-MTR-001",
      lineId: "line-1",
      parentId: "extruder",
      order: 0,
      name: "Main Drive Motor",
      type: "Motor",
      manufacturer: "Unknown",
      model: "Unknown",
      serial: "Unknown",
      status: "In Service",
      criticality: "A",
      verificationState: "Needs Research",
      completeness: 18,
      specs: [
        { label: "Power", value: "Unknown", unit: "HP", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Voltage", value: "Unknown", unit: "V", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "RPM", value: "Unknown", unit: "RPM", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Frame", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Torque", value: "Unknown", unit: "Nm", class: "OEM SPEC", verification: "Unknown" }
      ],
      capabilities: [],
      bom: []
    },
    {
      id: "ext-gearbox",
      tag: "L01-EXT-GBX-001",
      lineId: "line-1",
      parentId: "extruder",
      order: 1,
      name: "Extruder Gearbox",
      type: "Gearbox",
      manufacturer: "Unknown",
      model: "Unknown",
      serial: "Unknown",
      status: "In Service",
      criticality: "A",
      verificationState: "Needs Research",
      completeness: 15,
      specs: [
        { label: "Ratio", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Continuous output torque", value: "Unknown", unit: "Nm", class: "OEM SPEC", verification: "Unknown" },
        { label: "Peak output torque", value: "Unknown", unit: "Nm", class: "OEM SPEC", verification: "Unknown" },
        { label: "Rated axial thrust", value: "Unknown", unit: "kN", class: "OEM SPEC", verification: "Unknown" }
      ],
      capabilities: [],
      bom: [{ partId: "p-bearing-1", qty: 2, locationNote: "EXAMPLE association only" }]
    },
    {
      id: "ext-screw",
      tag: "L01-EXT-SCR-001",
      lineId: "line-1",
      parentId: "extruder",
      order: 2,
      name: "Screw Set",
      type: "Screw/Screw Set",
      manufacturer: "Unknown",
      model: "Unknown",
      serial: "Unknown",
      status: "In Service",
      criticality: "A",
      verificationState: "Needs Research",
      completeness: 10,
      specs: [
        { label: "Configuration", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Diameter", value: "Unknown", unit: "mm", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Effective length", value: "Unknown", unit: "mm", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Metallurgy", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "PVC compatibility", value: "Unknown", class: "PLANT VALIDATED", verification: "Unknown" },
        { label: "CPVC compatibility", value: "Unknown", class: "PLANT VALIDATED", verification: "Unknown" }
      ],
      capabilities: [],
      bom: []
    },
    {
      id: "ext-barrel",
      tag: "L01-EXT-BAR-001",
      lineId: "line-1",
      parentId: "extruder",
      order: 3,
      name: "Barrel",
      type: "Barrel",
      manufacturer: "Unknown",
      model: "Unknown",
      serial: "Unknown",
      status: "In Service",
      criticality: "A",
      verificationState: "Needs Research",
      completeness: 10,
      specs: [
        { label: "Bore diameter", value: "Unknown", unit: "mm", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Length", value: "Unknown", unit: "mm", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Material / liner", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "Heating zones", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" },
        { label: "PVC compatibility", value: "Unknown", class: "PLANT VALIDATED", verification: "Unknown" },
        { label: "CPVC compatibility", value: "Unknown", class: "PLANT VALIDATED", verification: "Unknown" }
      ],
      capabilities: [],
      bom: []
    }
  ],
  parts: [
    {
      id: "p-bearing-1",
      internalPn: "EXAMPLE-001",
      oemPn: "6208-2RS",
      description: "Example bearing record",
      manufacturer: "EXAMPLE",
      category: "Bearing",
      criticality: "A",
      stockQty: 0,
      minQty: 1,
      location: "Unknown",
      preferredVendorId: "vendor-1",
      vendorSku: "",
      leadTime: "Unknown",
      alternates: []
    }
  ],
  vendors: [
    {
      id: "vendor-1",
      name: "Example Industrial Supplier",
      website: "",
      contact: "",
      phone: "",
      email: "",
      preferred: false,
      notes: "Seed record only. Replace with actual approved vendor."
    }
  ],
  audit: [
    {
      id: "audit-seed",
      at: new Date().toISOString(),
      user: "System",
      action: "Seeded pilot",
      entity: "Line 1",
      detail: "Created example Line 1 structure. Technical values remain Unknown until verified."
    }
  ]
};

export const equipmentTemplates: Record<string, string[]> = {
  Extruder: ["OEM", "Model", "Serial", "Year", "Screw configuration", "Screw diameter", "L/D", "Max screw RPM", "Continuous torque", "Peak torque", "Thrust capacity", "Gearbox ratio", "Motor HP", "Heating zones", "Cooling zones", "PVC capability", "CPVC capability", "Rated throughput", "Plant validated throughput", "Max pressure", "Temperature limits"],
  Motor: ["Manufacturer", "Model", "Serial", "HP / kW", "Voltage", "Phase", "FLA", "RPM", "Frequency", "Frame", "Torque", "Service factor", "Insulation class", "Enclosure", "Mounting", "Shaft diameter", "Shaft length", "Bearings", "Encoder", "Brake", "VFD", "Replacement model"],
  Gearbox: ["Manufacturer", "Model", "Serial", "Ratio", "Input RPM", "Output RPM", "Rated input power", "Continuous output torque", "Peak torque", "Service factor", "Oil type", "Oil capacity", "Rated axial thrust"],
  "Screw/Screw Set": ["Manufacturer", "Part number", "Serial", "Configuration", "Diameter", "Effective length", "L/D", "Metallurgy", "Coating / treatment", "PVC compatibility", "CPVC compatibility", "Max RPM", "Torque rating", "New dimensions", "Wear measurements", "Allowable limits", "Installed date", "Operating hours"],
  Barrel: ["Manufacturer / OEM", "Part number", "Bore diameter", "Length", "Construction / material", "Liner / surface treatment", "PVC compatibility", "CPVC compatibility", "Heating zones", "Cooling zones", "Thermocouple type/location", "New bore dimensions", "Current measurements", "Wear limits"],
  Bearing: ["Manufacturer", "Part number", "Bore", "OD", "Width", "Seal / shield", "Clearance", "Load rating", "Speed rating", "Lubricant"],
  "Vacuum Pump": ["Manufacturer", "Model", "Serial", "Motor power", "Capacity", "Ultimate vacuum", "Seal type", "Operating range"],
  Puller: ["Manufacturer", "Model", "Belt size", "Drive motor", "Gearbox", "Max pull force", "Max line speed", "Cylinder type"],
  Saw: ["Manufacturer", "Model", "Blade specification", "Blade motor", "Carriage drive", "Encoder", "Max pipe diameter", "Max wall", "Cycle limit"],
  "Belling Equipment": ["Manufacturer", "Model", "Heating configuration", "Tooling", "Hydraulics", "Pneumatics", "Cooling", "Product range"],
  Heater: ["Manufacturer", "Part number", "Voltage", "Wattage", "Diameter", "Length", "Lead style", "Max sheath temperature"],
  Sensor: ["Manufacturer", "Model", "Type", "Range", "Output", "Supply voltage", "Connection", "Accuracy"],
  Cylinder: ["Manufacturer", "Model", "Bore", "Stroke", "Rod diameter", "Mounting", "Pressure rating"],
  Generic: ["Manufacturer", "Model", "Serial", "Description"]
};
